import { useState, useEffect, useCallback } from 'react';
import { Form, message } from 'antd';
import { lifetreeApi } from '../../api/lifetreeApi';
import { useToast } from '../../components/Toast';
import { NodeCharacterNPCPlan, NodeDetailResponse, 
  LifetreeDetailResponse, 
  UpdateNodeRequest,
  CreateCharacterRequest,
  CreateCharacterStateRequest,
  CreateScenarioRequest,
  CreateScenarioSampleRequest,
  CreateScenarioSampleResponse,
  NodeScenario,
  CreateScenarioImageResponse,
  UpdateScenarioRequest,
  StateImage,
  CreateScenarioImagePromptResponse,
  NodeScenarioProposal,
  CharacterStateWithRelationships
} from '../../api/models/Lifetree';
import { CharacterStateRow, NPCRow } from './types';

export const useNodeDetail = (lifeTreeId: string | undefined, nodeId: string | undefined) => {
  const toast = useToast();
  const [form] = Form.useForm();
  const [createScenarioForm] = Form.useForm();
  const [sampleScenarioForm] = Form.useForm();
  const [editScenarioForm] = Form.useForm();

  // Data states
  const [nodeDetail, setNodeDetail] = useState<NodeDetailResponse | null>(null);
  const [lifetreeDetail, setLifetreeDetail] = useState<LifetreeDetailResponse | null>(null);
  const [characterStatesData, setCharacterStatesData] = useState<CharacterStateRow[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasStateChanges, setHasStateChanges] = useState(false);
  const [pendingCharacterStateIds, setPendingCharacterStateIds] = useState<string[]>([]);
  const [activatingNode, setActivatingNode] = useState(false);
  const [activatingStates, setActivatingStates] = useState<Set<string>>(new Set());

  // Planning state
  const [planningCharacters, setPlanningCharacters] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editablePlanCharacters, setEditablePlanCharacters] = useState<NodeCharacterNPCPlan[]>([]);
  const [creatingCharactersFromPlan, setCreatingCharactersFromPlan] = useState(false);
  const [creatingStatesFromPlan, setCreatingStatesFromPlan] = useState(false);
  const [deployingRelationships, setDeployingRelationships] = useState(false);
  const [deployingImages, setDeployingImages] = useState(false);
  const [deployingFull, setDeployingFull] = useState(false);
  const [formalizingCharacters, setFormalizingCharacters] = useState(false);
  const [deployingCharacters, setDeployingCharacters] = useState(false);
  const [resettingCharacters, setResettingCharacters] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState<{
    total: number;
    current: number;
    status: string;
  } | null>(null);

  const [deployingScenarios, setDeployingScenarios] = useState(false);
  const [deploymentScenarioProgress, setDeploymentScenarioProgress] = useState<{
    total: number;
    current: number;
    status: string;
  } | null>(null);

  const [autoDeploying, setAutoDeploying] = useState(false);
  const [autoDeploymentProgress, setAutoDeploymentProgress] = useState<{
    step: number;
    totalSteps: number;
    status: string;
    percentage: number;
  } | null>(null);

  // Modal states
  const [showCreateCharacterModal, setShowCreateCharacterModal] = useState(false);
  const [creatingCharacter, setCreatingCharacter] = useState(false);
  const [creatingState, setCreatingState] = useState(false);
  const [selectedCharacterForState, setSelectedCharacterForState] = useState<string | null>(null);
  const [methodSelectionModalOpen, setMethodSelectionModalOpen] = useState(false);
  const [createNewStateModalOpen, setCreateNewStateModalOpen] = useState(false);
  const [aiDescriptionModalOpen, setAIDescriptionModalOpen] = useState(false);
  const [stateInitialData, setStateInitialData] = useState<Partial<CreateCharacterStateRequest> | undefined>(undefined);

  const [showCreateScenarioModal, setShowCreateScenarioModal] = useState(false);
  const [showSampleScenarioModal, setShowSampleScenarioModal] = useState(false);
  const [creatingScenario, setCreatingScenario] = useState(false);
  const [generatingSample, setGeneratingSample] = useState(false);
  const [sampleScenarioData, setSampleScenarioData] = useState<CreateScenarioSampleResponse | null>(null);

  const [showScenarioImagePreviewModal, setShowScenarioImagePreviewModal] = useState(false);
  const [selectedScenarioForImage, setSelectedScenarioForImage] = useState<NodeScenario | null>(null);
  const [generatedScenarioImage, setGeneratedScenarioImage] = useState<CreateScenarioImageResponse | null>(null);
  const [creatingScenarioImage, setCreatingScenarioImage] = useState(false);
  const [approvingScenarioImage, setApprovingScenarioImage] = useState(false);

  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState<NodeScenario | null>(null);
  const [updatingScenario, setUpdatingScenario] = useState(false);

  const [activatingScenarioIds, setActivatingScenarioIds] = useState<Set<string>>(new Set());
  const [quickGenScenarioIds, setQuickGenScenarioIds] = useState<Set<string>>(new Set());
  const [npcRows, setNpcRows] = useState<NPCRow[]>([{ id: '1', characterId: null, stateId: null }]);

  const [showCreateScenarioImageModal, setShowCreateScenarioImageModal] = useState(false);
  const [generatingScenarioPrompt, setGeneratingScenarioPrompt] = useState(false);

  const [proposingScenarios, setProposingScenarios] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [scenarioProposals, setScenarioProposals] = useState<NodeScenarioProposal[]>([]);
  const [showScenarioProposalsModal, setShowScenarioProposalsModal] = useState(false);
  const [processingProposalIds, setProcessingProposalIds] = useState<Set<string>>(new Set());
  const [createdProposalIds, setCreatedProposalIds] = useState<Set<string>>(new Set());

  // Node Image states
  const [nodeImages, setNodeImages] = useState<StateImage[]>([]);
  const [aiConcept, setAiConcept] = useState<CreateScenarioImagePromptResponse | null>(null);
  const [isGeneratingConcept, setIsGeneratingConcept] = useState(false);
  const [isGeneratingNodeImage, setIsGeneratingNodeImage] = useState(false);
  const [isFetchingNodeImages, setIsFetchingNodeImages] = useState(false);

  const processCharacterStatesData = useCallback((node: NodeDetailResponse, lifetree: LifetreeDetailResponse) => {
    const characterStatesRows: CharacterStateRow[] = [];
    const stateIdsToProcess = pendingCharacterStateIds.length > 0 ? pendingCharacterStateIds : node.characterStateIds;

    stateIdsToProcess.forEach(stateId => {
      for (const character of lifetree.characters) {
        const state = character.states.find(s => s.id === stateId);
        if (state) {
          characterStatesRows.push({
            key: state.id,
            characterId: character.id,
            characterName: character.name,
            characterType: character.type,
            characterAvatar: state.imageUrl || character.imageUrl,
            stateId: state.id,
            stateName: state.name,
            stateAge: state.age,
            relationshipCount: 0,
            relationships: [],
          });
          break;
        }
      }
    });

    characterStatesRows.sort((a, b) => {
      if (a.characterType === 'main' && b.characterType !== 'main') return -1;
      if (a.characterType !== 'main' && b.characterType === 'main') return 1;
      return a.characterName.localeCompare(b.characterName);
    });

    setCharacterStatesData(characterStatesRows);
  }, [pendingCharacterStateIds]);

  const fetchData = useCallback(async (silent = false) => {
    if (!lifeTreeId || !nodeId) return;

    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [nodeResponse, lifetreeResponse] = await Promise.all([
        lifetreeApi.getNodeDetail(lifeTreeId, nodeId),
        lifetreeApi.getLifetreeDetail(lifeTreeId),
      ]);

      setNodeDetail(nodeResponse);
      setLifetreeDetail(lifetreeResponse);

      form.setFieldsValue({
        title: nodeResponse.title,
        description: nodeResponse.description,
        level: nodeResponse.level,
        requiredExp: nodeResponse.requiredExp,
        tags: nodeResponse.tags,
        direction: nodeResponse.direction,
        summary: nodeResponse.summary,
      });

      setPendingCharacterStateIds(nodeResponse.characterStateIds);
    } catch (error: any) {
      toast.error('Failed to fetch node detail', error.response?.data?.messages?.[0] || error.message);
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [lifeTreeId, nodeId, form, toast]);

  // Handler functions
  const handlePlanCharacters = async () => {
    if (!lifeTreeId || !nodeId) return;
    setPlanningCharacters(true);
    try {
      const language = lifetreeDetail?.language || 'en';
      const result = await lifetreeApi.proposeNodeCharacters(lifeTreeId, nodeId, language);
      
      setEditablePlanCharacters(result.characters);
      setPlanModalOpen(true);
    } catch (error: any) {
      toast.error('Lỗi khi lập kế hoạch nhân vật', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setPlanningCharacters(false);
    }
  };

  const updatePlanCharacter = (index: number, updatedChar: NodeCharacterNPCPlan) => {
    setEditablePlanCharacters(prev => {
      const newChars = [...prev];
      newChars[index] = updatedChar;
      return newChars;
    });
  };

  const handleFormalizeCharacters = async () => {
    if (!lifeTreeId || !nodeId || editablePlanCharacters.length === 0 || !nodeDetail) return;
    setFormalizingCharacters(true);
    try {
      // Regenerate planRefinement text from edited characters
      let proposalText = '';
      const mainChar = editablePlanCharacters.find(c => c.isMain);
      if (mainChar) {
        proposalText += `### Nhân vật chính (Main):\n- **Tên**: ${mainChar.name}\n- **Giới tính**: ${mainChar.gender}\n- **Mô tả**: ${mainChar.stateDescription}\n\n`;
      }
      
      proposalText += `### Danh sách NPC:\n`;
      editablePlanCharacters.filter(c => !c.isMain).forEach(npc => {
        proposalText += `- **${npc.name}** (${npc.isNew ? 'Mới' : 'Cũ'} - ${npc.gender}): ${npc.stateDescription}\n`;
      });

      // Step 1: Update node plan and direction
      const updateData: UpdateNodeRequest = {
        plan: { characters: editablePlanCharacters },
        direction: proposalText,
      };

      await lifetreeApi.updateNode(lifeTreeId, nodeId, updateData);
      
      toast.success('Cập nhật kế hoạch và định hướng thành công!', 'Bấm "Triển khai nhân vật" để tạo dữ liệu.');
      setPlanModalOpen(false);
      
      // Cập nhật state cục bộ thay vì fetch lại toàn bộ
      setNodeDetail(prev => prev ? { 
        ...prev, 
        plan: updateData.plan, 
        direction: updateData.direction 
      } : prev);
    } catch (error: any) {
      toast.error('Lỗi khi cập nhật dữ liệu', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setFormalizingCharacters(false);
    }
  };

  const handleDeployCharacters = async (nodeDetailOverride?: NodeDetailResponse | null, lifetreeDetailOverride?: LifetreeDetailResponse | null) => {
    const effectiveNodeDetail = nodeDetailOverride || nodeDetail;
    const effectiveLifetreeDetail = lifetreeDetailOverride || lifetreeDetail;
    if (!lifeTreeId || !nodeId || !effectiveNodeDetail?.plan) {
      toast.error('Không tìm thấy kế hoạch nhân vật (Plan)');
      return;
    }

    setDeployingCharacters(true);
    setDeployingFull(true);
    try {
      // Step 1: Create Characters (Phase 2)
      await handleCreateCharactersFromPlan(true, effectiveNodeDetail, effectiveLifetreeDetail);

      // Refresh data after potentially creating characters
      const freshLifetreeAfterChars = await lifetreeApi.getLifetreeDetail(lifeTreeId);

      // Step 2: Create States (Phase 3)
      await handleCreateStatesFromPlan(true, effectiveNodeDetail, freshLifetreeAfterChars);

      // Step 3: Create Relationships (Phase 4)
      // Refresh node detail to get new characterStateIds after Step 2
      const freshNodeAfterStates = await lifetreeApi.getNodeDetail(lifeTreeId, nodeId);
      await handleDeployRelationships(true, freshNodeAfterStates, freshLifetreeAfterChars);

      // Step 4: Create Images & Activate (Phase 5)
      // Use the refreshed node detail which has the newly created character states
      await handleDeployImages(true, freshNodeAfterStates);

      toast.success('Triển khai trọn gói nhân vật thành công!');
      await fetchData(true);
    } catch (error: any) {
      console.error('Error in full character deployment:', error);
      toast.error('Lỗi khi triển khai trọn gói', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setDeployingCharacters(false);
      setDeployingFull(false);
      setDeploymentProgress(null);
    }
  };

  const handleResetCharacters = async () => {
    if (!lifeTreeId || !nodeId) return;
    
    setResettingCharacters(true);
    try {
      await lifetreeApi.resetNodeCharacters(lifeTreeId, nodeId);
      toast.success('Đã reset nhân vật thành công!');
      await fetchData(true);
    } catch (error: any) {
      toast.error('Lỗi khi reset nhân vật', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setResettingCharacters(false);
    }
  };

  const handleCreateCharactersFromPlan = async (isOrchestrated = false, nodeDetailOverride?: NodeDetailResponse | null, lifetreeDetailOverride?: LifetreeDetailResponse | null) => {
    const plan = nodeDetailOverride?.plan || nodeDetail?.plan;
    const currentLifetree = lifetreeDetailOverride || lifetreeDetail;
    if (!lifeTreeId || !nodeId || !plan || !plan.characters) {
      if (!isOrchestrated) toast.error('Không tìm thấy kế hoạch nhân vật (Plan)');
      return;
    }

    if (!isOrchestrated) setCreatingCharactersFromPlan(true);
    setDeploymentProgress({ 
      total: plan.characters.length, 
      current: 0, 
      status: 'Bắt đầu khởi tạo nhân vật từ Plan...' 
    });

    try {
      const characters = plan.characters as NodeCharacterNPCPlan[];
      
      for (let i = 0; i < characters.length; i++) {
        const charPlan = characters[i];
        setDeploymentProgress(prev => ({ 
          ...prev!, 
          current: i + 1, 
          status: `[Bước 1/4] Đang xử lý ${charPlan.name} (${i + 1}/${characters.length})...` 
        }));

        try {
          if (charPlan.isMain) {
            // Case 1: Main Character
            const existingMain = currentLifetree?.characters.find(c => c.type === 'main');
            if (!existingMain) {
              setDeploymentProgress(prev => ({ ...prev!, status: `Đang khởi tạo nhân vật chính: {{userName}}...` }));
              await lifetreeApi.createCharacter(lifeTreeId, {
                name: '{{userName}}',
                type: 'main',
                gender: charPlan.gender
              });
            } else {
              setDeploymentProgress(prev => ({ ...prev!, status: `Nhân vật chính đã tồn tại. Bỏ qua.` }));
            }
          } else if (charPlan.isNew) {
            // Case 2: New NPC
            const existingNPC = currentLifetree?.characters.find(c => c.name === charPlan.name && c.type === 'npc');
            if (!existingNPC) {
              setDeploymentProgress(prev => ({ ...prev!, status: `Đang khởi tạo NPC mới: ${charPlan.name}...` }));
              await lifetreeApi.createCharacter(lifeTreeId, {
                name: charPlan.name,
                type: 'npc',
                gender: charPlan.gender
              });
            } else {
              setDeploymentProgress(prev => ({ ...prev!, status: `NPC ${charPlan.name} đã tồn tại. Bỏ qua.` }));
            }
          }
        } catch (err: any) {
          console.error(`Error creating character ${charPlan.name}:`, err);
          toast.error(`Lỗi khi tạo nhân vật ${charPlan.name}`, err.response?.data?.messages?.[0] || err.message);
        }
      }

      if (!isOrchestrated) {
        toast.success('Khởi tạo nhân vật thành công!');
        await fetchData(true);
      }
    } catch (error: any) {
      if (!isOrchestrated) toast.error('Lỗi khi thực hiện Phase 2', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      if (!isOrchestrated) {
        setCreatingCharactersFromPlan(false);
        setDeploymentProgress(null);
      }
    }
  };

  const findPreviousStateId = (characterId: string, nodeId: string, treeData?: typeof lifetreeDetail): string | null => {
    const data = treeData || lifetreeDetail;
    if (!data || !nodeId) return null;

    // Build parent map to trace back to root
    const parentMap = new Map<string, string>();
    data.nodes.forEach(n => {
      n.nextNodeIds.forEach(nextId => {
        parentMap.set(nextId, n.id);
      });
    });

    let currentId = parentMap.get(nodeId);
    while (currentId) {
      const ancestorNode = data.nodes.find(n => n.id === currentId);
      if (ancestorNode) {
        // Find if this ancestor has a state for this character
        const char = data.characters.find(c => c.id === characterId);
        if (char) {
          const stateInAncestor = char.states.find(s => ancestorNode.characterStateIds.includes(s.id));
          if (stateInAncestor) {
            return stateInAncestor.id;
          }
        }
      }
      currentId = parentMap.get(currentId);
    }

    return null;
  };

  const handleCreateStatesFromPlan = async (isOrchestrated = false, nodeDetailOverride?: NodeDetailResponse | null, lifetreeDetailOverride?: LifetreeDetailResponse | null) => {
    const plan = nodeDetailOverride?.plan || nodeDetail?.plan;
    if (!lifeTreeId || !nodeId || !plan || !plan.characters) {
      if (!isOrchestrated) toast.error('Không tìm thấy kế hoạch nhân vật (Plan)');
      return;
    }

    if (!isOrchestrated) setCreatingStatesFromPlan(true);
    setDeploymentProgress({ 
      total: plan.characters.length, 
      current: 0, 
      status: 'Bắt đầu khởi tạo Character State từ Plan...' 
    });

    try {
      const planCharacters = plan.characters as NodeCharacterNPCPlan[];

      // When orchestrated, fetch fresh data from API since React state may not have updated yet
      let currentLifetreeDetail = lifetreeDetailOverride || lifetreeDetail;
      let currentStateIds = nodeDetailOverride ? [...nodeDetailOverride.characterStateIds] : [...pendingCharacterStateIds];
      
      if (isOrchestrated && !nodeDetailOverride) {
        const [freshLifetree, freshNode] = await Promise.all([
          lifetreeApi.getLifetreeDetail(lifeTreeId),
          lifetreeApi.getNodeDetail(lifeTreeId, nodeId)
        ]);
        currentLifetreeDetail = freshLifetree;
        currentStateIds = [...freshNode.characterStateIds];
      }

      const newStateIds: string[] = [...currentStateIds];
      let hasNewStates = false;

      for (let i = 0; i < planCharacters.length; i++) {
        const charPlan = planCharacters[i];
        
        // Find Character ID from fresh data
        const character = currentLifetreeDetail?.characters.find(c => 
          (charPlan.isMain && c.type === 'main') || (!charPlan.isMain && c.name === charPlan.name)
        );

        if (!character) {
          toast.error(`Không tìm thấy nhân vật ${charPlan.name} trong hệ thống. Vui lòng thực hiện Bước 1 (Tạo nhân vật) trước.`);
          continue;
        }

        // Check if state already exists in this node for this character
        const existingStateInNode = character.states.find(s => currentStateIds.includes(s.id));
        if (existingStateInNode) {
          setDeploymentProgress(prev => ({ 
            ...prev!, 
            current: i + 1, 
            status: `[Bước 2/4] State cho ${charPlan.name} đã tồn tại trong node. Bỏ qua.` 
          }));
          continue;
        }

        setDeploymentProgress(prev => ({ 
          ...prev!, 
          current: i + 1, 
          status: `[Bước 2/4] Đang sinh dữ liệu state cho ${charPlan.name}...` 
        }));

        const previousStateId = findPreviousStateId(character.id, nodeId, currentLifetreeDetail ?? undefined);
        
        try {
          let aiResult;
          if (previousStateId) {
            aiResult = await lifetreeApi.createCharacterNextStateCompletion(lifeTreeId, character.id, { 
              description: charPlan.stateDescription, 
              previousStateId 
            });
          } else {
            aiResult = await lifetreeApi.createCharacterStateCompletion(lifeTreeId, character.id, { 
              description: charPlan.stateDescription 
            });
          }

          setDeploymentProgress(prev => ({ ...prev!, status: `Đang lưu bản ghi state cho ${charPlan.name}...` }));
          
          // Create the state
          const newState = await lifetreeApi.createCharacterState(lifeTreeId, character.id, {
            name: aiResult.name,
            age: aiResult.age,
            alias: aiResult.alias,
            occupation: aiResult.occupation,
            appearance: aiResult.appearance,
            innerSelf: aiResult.innerSelf,
            relationshipStyles: aiResult.relationshipStyles,
            description: aiResult.description,
            displayedDescription: aiResult.displayedDescription
          });

          newStateIds.push(newState.id);
          hasNewStates = true;
          
        } catch (err: any) {
          console.error(`Error creating state for ${charPlan.name}:`, err);
          toast.error(`Lỗi khi tạo state cho ${charPlan.name}`, err.response?.data?.messages?.[0] || err.message);
        }
      }

      if (hasNewStates) {
        setDeploymentProgress(prev => ({ ...prev!, status: '[Bước 2/4] Đang cập nhật danh sách state cho Node...' }));
        
        // Update node with all new state IDs
        const updateData: UpdateNodeRequest = {
          characterStateIds: newStateIds,
        };
        await lifetreeApi.updateNode(lifeTreeId, nodeId, updateData);
        
        if (!isOrchestrated) {
          toast.success('Khởi tạo Character State thành công!');
          await fetchData(true);
        }
      } else if (!isOrchestrated) {
        toast.info('Không có Character State mới nào được tạo.');
      }
    } catch (error: any) {
      if (!isOrchestrated) toast.error('Lỗi khi thực hiện Phase 3', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      if (!isOrchestrated) {
        setCreatingStatesFromPlan(false);
        setDeploymentProgress(null);
      }
    }
  };

  const handleDeployRelationships = async (isOrchestrated = false, nodeDetailOverride?: NodeDetailResponse | null, lifetreeDetailOverride?: LifetreeDetailResponse | null) => {
    if (!lifeTreeId || !nodeId) return;

    if (!isOrchestrated) setDeployingRelationships(true);
    setDeploymentProgress({ 
      total: 1, 
      current: 0, 
      status: '[Bước 3/4] Đang phân tích định hướng và thiết lập quan hệ...' 
    });

    try {
      const language = (lifetreeDetailOverride || lifetreeDetail)?.language || 'en';
      const refinedPlan = (nodeDetailOverride || nodeDetail)?.direction || ''; // AI needs text description for context

      await lifetreeApi.generateNodeRelationships(lifeTreeId, nodeId, refinedPlan, language);
      
      if (!isOrchestrated) {
        toast.success('Thiết lập mối quan hệ thành công!');
        await fetchData(true);
      }
    } catch (error: any) {
      if (!isOrchestrated) toast.error('Lỗi khi thiết lập quan hệ', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      if (!isOrchestrated) {
        setDeployingRelationships(false);
        setDeploymentProgress(null);
      }
    }
  };

  const handleDeployImages = async (isOrchestrated = false, nodeDetailOverride?: NodeDetailResponse | null) => {
    if (!lifeTreeId || !nodeId || !(nodeDetailOverride || nodeDetail)) return;

    // Refresh nodeDetail to get the latest states created in Phase 3
    let currentNodeDetail = nodeDetailOverride || nodeDetail;
    if (isOrchestrated && !nodeDetailOverride) {
      currentNodeDetail = await lifetreeApi.getNodeDetail(lifeTreeId, nodeId);
    }

    if (!currentNodeDetail) return;
    const statesToGenerate = currentNodeDetail.characterStates.filter(s => !s.imageUrl);
    if (statesToGenerate.length === 0) {
      if (!isOrchestrated) toast.info('Tất cả nhân vật đã có ảnh.');
      return;
    }

    if (!isOrchestrated) setDeployingImages(true);
    setDeploymentProgress({
      total: statesToGenerate.length,
      current: 0,
      status: `[Bước 4/4] Bắt đầu tạo ảnh cho ${statesToGenerate.length} nhân vật...`
    });

    try {
      let current = 0;
      for (const state of statesToGenerate) {
        current++;
        setDeploymentProgress({
          total: statesToGenerate.length,
          current: current - 1,
          status: `[Bước 4/4] Đang tạo prompt cho ${state.name} (${current}/${statesToGenerate.length})...`
        });

        // 5.1 Create Prompt
        const promptRes = await lifetreeApi.createStateImagePrompt(lifeTreeId, state.characterId, state.id, {});
        
        setDeploymentProgress({
          total: statesToGenerate.length,
          current: current - 1,
          status: `[Bước 4/4] Đang vẽ ảnh cho ${state.name} (${current}/${statesToGenerate.length})...`
        });

        // 5.2 Create Image
        const imgRes = await lifetreeApi.createStateImageWithPredefinedPrompt(lifeTreeId, state.characterId, state.id, {
          description: promptRes.technicalPrompt || promptRes.description
        });

        setDeploymentProgress({
          total: statesToGenerate.length,
          current: current - 1,
          status: `[Bước 4/4] Đang phê duyệt ảnh cho ${state.name} (${current}/${statesToGenerate.length})...`
        });

        // 5.3 Approve Image
        await lifetreeApi.approveStateImage(lifeTreeId, state.characterId, state.id, {
          imageId: imgRes.id
        });

        // 5.4 Activate State
        setDeploymentProgress({
          total: statesToGenerate.length,
          current: current - 1,
          status: `[Bước 4/4] Đang kích hoạt ${state.name} (${current}/${statesToGenerate.length})...`
        });
        await lifetreeApi.activateState(lifeTreeId, state.characterId, state.id);

        setDeploymentProgress({
          total: statesToGenerate.length,
          current: current,
          status: `[Bước 4/4] Đã hoàn thành ${state.name} (${current}/${statesToGenerate.length})`
        });
      }

      if (!isOrchestrated) {
        toast.success('Đã hoàn tất tạo và phê duyệt ảnh cho các nhân vật!');
        await fetchData(true);
      }
    } catch (error: any) {
      console.error('Error deploying images:', error);
      if (!isOrchestrated) toast.error('Lỗi khi triển khai tạo ảnh', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      if (!isOrchestrated) {
        setDeployingImages(false);
        setDeploymentProgress(null);
      }
    }
  };

  const handleAutoDeployNode = async () => {
    if (!lifeTreeId || !nodeId) return;

    setAutoDeploying(true);
    const totalSteps = 6;
    let currentStep = 0;

    const updateAutoProgress = (status: string, percentage: number = 0) => {
      setAutoDeploymentProgress({
        step: currentStep,
        totalSteps,
        status,
        percentage
      });
    };

    try {
      // Step 1: Data Refresh
      currentStep = 1;
      updateAutoProgress('Đang đồng bộ dữ liệu ban đầu...', 10);
      await fetchData(true);
      
      // Step 2: Planning (if not exists)
      currentStep = 2;
      let currentNode = (await lifetreeApi.getNodeDetail(lifeTreeId, nodeId)); // Get freshest
      if (!currentNode.plan) {
        updateAutoProgress('Đang lập kế hoạch nhân vật (AI)...', 20);
        const language = lifetreeDetail?.language || 'vi';
        const planResult = await lifetreeApi.proposeNodeCharacters(lifeTreeId, nodeId, language);
        
        let proposalText = '';
        const mainChar = planResult.characters.find(c => c.isMain);
        if (mainChar) {
          proposalText += `### Nhân vật chính (Main):\n- **Tên**: ${mainChar.name}\n- **Giới tính**: ${mainChar.gender}\n- **Mô tả**: ${mainChar.stateDescription}\n\n`;
        }
        
        proposalText += `### Danh sách NPC:\n`;
        planResult.characters.filter(c => !c.isMain).forEach(npc => {
          proposalText += `- **${npc.name}** (${npc.isNew ? 'Mới' : 'Cũ'} - ${npc.gender}): ${npc.stateDescription}\n`;
        });

        updateAutoProgress('Đang lưu kế hoạch và định hướng...', 40);
        await lifetreeApi.updateNode(lifeTreeId, nodeId, {
          plan: { characters: planResult.characters },
          direction: proposalText,
        });
        await fetchData(true);
      } else {
        updateAutoProgress('Kế hoạch nhân vật đã tồn tại. Bỏ qua.', 100);
      }

      // Step 3: Deploy Characters
      currentStep = 3;
      updateAutoProgress('Đang triển khai trọn gói nhân vật (Nhân vật, State, Quan hệ, Ảnh)...', 0);
      
      // Get freshest data before deploying
      const [freshNode, freshLifetree] = await Promise.all([
        lifetreeApi.getNodeDetail(lifeTreeId, nodeId),
        lifetreeApi.getLifetreeDetail(lifeTreeId)
      ]);
      await handleDeployCharacters(freshNode, freshLifetree); 
      await fetchData(true);

      // Step 4: Propose & Create Scenarios
      currentStep = 4;
      updateAutoProgress('Đang lấy dữ liệu Node mới nhất...', 0);
      const [freshNode4, freshLifetree4] = await Promise.all([
        lifetreeApi.getNodeDetail(lifeTreeId, nodeId),
        lifetreeApi.getLifetreeDetail(lifeTreeId)
      ]);
      
      if (freshNode4.scenarios.length === 0) {
        updateAutoProgress('Đang gợi ý các kịch bản (Scenario)...', 20);
        const scenarioRes = await lifetreeApi.proposeNodeScenarios(lifeTreeId, nodeId, freshLifetree4?.language || 'vi');
        
        for (let i = 0; i < scenarioRes.proposals.length; i++) {
          const proposal = scenarioRes.proposals[i];
          updateAutoProgress(`Đang tạo kịch bản: ${proposal.title} (${i + 1}/${scenarioRes.proposals.length})...`, 40 + (i / scenarioRes.proposals.length) * 40);
          
          const sampleData: CreateScenarioSampleRequest = {
            directive: proposal.directive,
            characterStateIds: proposal.characterStateIds || [],
          };
          const sampleResponse = await lifetreeApi.createScenarioSample(lifeTreeId, nodeId, sampleData);

          const scenarioData: CreateScenarioRequest = {
            name: sampleResponse.name,
            description: sampleResponse.description || undefined,
            location: sampleResponse.location,
            locationDescription: sampleResponse.locationDescription || undefined,
            time: sampleResponse.time,
            npcIds: sampleResponse.npcIds || [],
            openingScene: sampleResponse.openingScene,
            openingLines: sampleResponse.openingLines || [],
            firstMission: sampleResponse.firstMission || undefined,
          };
          await lifetreeApi.createScenario(lifeTreeId, nodeId, scenarioData);
        }
        await fetchData(true);
      } else {
        updateAutoProgress('Kịch bản đã tồn tại. Bỏ qua.', 100);
      }

      // Step 5: Deploy Scenarios (Images & Activation)
      currentStep = 5;
      updateAutoProgress('Đang triển khai ảnh và kích hoạt các kịch bản...', 0);
      const freshNode5 = await lifetreeApi.getNodeDetail(lifeTreeId, nodeId);
      await handleDeployScenarios(freshNode5);
      await fetchData(true);

      // Step 6: Generate Summary
      currentStep = 6;
      updateAutoProgress('Đang tự động tóm tắt nội dung node...', 50);
      const freshLifetree6 = await lifetreeApi.getLifetreeDetail(lifeTreeId);
      await handleGenerateSummary(freshLifetree6);
      updateAutoProgress('Hoàn tất tóm tắt.', 100);

      toast.success('Triển khai tự động hoàn toàn thành công!', 'Node đã sẵn sàng để kích hoạt.');
      await fetchData(true);
    } catch (error: any) {
      console.error('Error in auto deploy:', error);
      toast.error('Lỗi trong quá trình triển khai tự động', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setAutoDeploying(false);
      setAutoDeploymentProgress(null);
    }
  };

  const handleSave = async () => {
    if (!nodeDetail || !lifeTreeId || !nodeId) return;

    try {
      const formValues = await form.validateFields();
      setSaving(true);

      const updateData: UpdateNodeRequest = {
        title: formValues.title,
        description: formValues.description || null,
        imageUrl: nodeDetail.imageUrl,
        level: formValues.level,
        requiredExp: formValues.requiredExp,
        tags: formValues.tags || [],
        direction: formValues.direction || null,
        summary: formValues.summary || null,
        plan: nodeDetail.plan,
        characterStateIds: pendingCharacterStateIds,
      };

      await lifetreeApi.updateNode(lifeTreeId, nodeId, updateData);
      
      // Cập nhật state cục bộ thay vì fetch lại toàn bộ
      setNodeDetail(prev => prev ? { ...prev, ...updateData } : prev);
      
      setHasChanges(false);
      setHasStateChanges(false);
      toast.success('Node updated successfully');
    } catch (error: any) {
      toast.error('Failed to update node', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetCoverImage = async (imageUrl: string) => {
    if (!nodeDetail || !lifeTreeId || !nodeId) return;
    try {
      const formValues = form.getFieldsValue();
      await lifetreeApi.updateNode(lifeTreeId, nodeId, {
        title: formValues.title || nodeDetail.title,
        description: formValues.description ?? nodeDetail.description,
        imageUrl,
        level: formValues.level ?? nodeDetail.level,
        requiredExp: formValues.requiredExp ?? nodeDetail.requiredExp,
        tags: formValues.tags ?? nodeDetail.tags,
        direction: formValues.direction ?? nodeDetail.direction,
        summary: formValues.summary ?? nodeDetail.summary,
        plan: nodeDetail.plan,
        characterStateIds: pendingCharacterStateIds,
      });
      
      // Cập nhật state cục bộ thay vì fetch lại
      setNodeDetail(prev => prev ? { ...prev, imageUrl } : prev);
      
      toast.success('Đã cập nhật ảnh bìa node');
    } catch (error: any) {
      toast.error('Lỗi cập nhật ảnh bìa', error.response?.data?.messages?.[0] || error.message);
    }
  };

  const handleActivateNode = async () => {
    if (!nodeDetail || !lifeTreeId || !nodeId) return;

    if (!nodeDetail.imageUrl) {
      toast.error('Cannot activate node', 'Node must have an image URL');
      return;
    }

    const inactiveStates = nodeDetail.characterStates.filter(state => state.status !== 'active');
    if (inactiveStates.length > 0) {
      toast.error('Cannot activate node', 'All character states in the node must be active first');
      return;
    }

    const hasActiveScenario = nodeDetail.scenarios.some(sc => sc.status === 'active');
    if (!hasActiveScenario) {
      toast.error('Cannot activate node', 'Node must have at least one active scenario');
      return;
    }

    try {
      setActivatingNode(true);
      await lifetreeApi.activateNode(lifeTreeId, nodeId);
      await fetchData(true);
      toast.success('Node activated successfully');
    } catch (error: any) {
      toast.error('Failed to activate node', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setActivatingNode(false);
    }
  };

  const handleActivateState = async (characterId: string, stateId: string) => {
    if (!lifeTreeId) return;

    try {
      setActivatingStates(prev => new Set(prev.add(stateId)));
      await lifetreeApi.activateState(lifeTreeId, characterId, stateId);
      await fetchData(true);
      toast.success('Character state activated successfully');
    } catch (error: any) {
      toast.error('Failed to activate character state', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setActivatingStates(prev => {
        const newSet = new Set(prev);
        newSet.delete(stateId);
        return newSet;
      });
    }
  };

  const handleCreateCharacter = async (data: CreateCharacterRequest) => {
    if (!lifeTreeId) return;

    setCreatingCharacter(true);
    try {
      const newCharacter = await lifetreeApi.createCharacter(lifeTreeId, data);
      const updatedLifetree = await lifetreeApi.getLifetreeDetail(lifeTreeId);
      setLifetreeDetail(updatedLifetree);
      setShowCreateCharacterModal(false);
      toast.success(`Nhân vật "${newCharacter.name}" đã được tạo thành công`);
    } catch (error: any) {
      toast.error('Tạo nhân vật thất bại', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      setCreatingCharacter(false);
    }
  };

  const handleMethodSelection = (method: 'manual' | 'ai') => {
    setMethodSelectionModalOpen(false);
    if (method === 'manual') {
      setStateInitialData(undefined);
      setCreateNewStateModalOpen(true);
    } else {
      setAIDescriptionModalOpen(true);
    }
  };

  const handleAICompletion = async (description: string, previousStateId?: string) => {
    if (!selectedCharacterForState || !lifeTreeId) return;

    try {
      setCreatingState(true);
      let aiResult;
      if (previousStateId) {
        aiResult = await lifetreeApi.createCharacterNextStateCompletion(lifeTreeId, selectedCharacterForState, { 
          description, 
          previousStateId 
        });
      } else {
        aiResult = await lifetreeApi.createCharacterStateCompletion(lifeTreeId, selectedCharacterForState, { description });
      }
      
      const initialData: Partial<CreateCharacterStateRequest> = {
        name: aiResult.name,
        age: aiResult.age,
        alias: aiResult.alias,
        occupation: aiResult.occupation,
        appearance: aiResult.appearance,
        innerSelf: aiResult.innerSelf,
        relationshipStyles: aiResult.relationshipStyles,
      };
      
      setStateInitialData(initialData);
      setAIDescriptionModalOpen(false);
      setCreateNewStateModalOpen(true);
      toast.success('AI đã tạo nguyên mẫu thành công! Vui lòng kiểm tra và chỉnh sửa nếu cần.');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.messages?.[0] || error?.message || 'Có lỗi xảy ra khi tạo nguyên mẫu bằng AI';
      toast.error('Tạo nguyên mẫu thất bại', errorMsg);
      throw error;
    } finally {
      setCreatingState(false);
    }
  };

  const handleCreateNewState = async (data: CreateCharacterStateRequest) => {
    if (!selectedCharacterForState || !lifeTreeId) return;

    try {
      setCreatingState(true);
      const newState = await lifetreeApi.createCharacterState(lifeTreeId, selectedCharacterForState, data);
      
      const newCharacterStateIds = [...pendingCharacterStateIds, newState.id];
      setPendingCharacterStateIds(newCharacterStateIds);
      setHasStateChanges(true);
      
      await fetchData(true);
      setCreateNewStateModalOpen(false);
      setSelectedCharacterForState(null);
      setStateInitialData(undefined);
      toast.success(`State "${newState.name}" đã được tạo thành công và thêm vào pending changes`);
    } catch (error) {
      console.error('Error creating state:', error);
      throw error;
    } finally {
      setCreatingState(false);
    }
  };

  const handleAddCharacterState = async (characterId: string, stateId: string) => {
    if (pendingCharacterStateIds.includes(stateId)) {
      message.warning('This character state is already selected');
      return;
    }

    const newCharacterStateIds = [...pendingCharacterStateIds, stateId];
    setPendingCharacterStateIds(newCharacterStateIds);
    setHasStateChanges(true);
    message.success('Character state added to pending changes');
  };

  const handleRemoveCharacterState = async (stateId: string) => {
    const newCharacterStateIds = pendingCharacterStateIds.filter(id => id !== stateId);
    setPendingCharacterStateIds(newCharacterStateIds);
    setHasStateChanges(true);
    message.success('Character state removed from pending changes');
  };

  const handleProposeScenarios = async () => {
    if (!lifeTreeId || !nodeId) return;

    try {
      setProposingScenarios(true);
      const response = await lifetreeApi.proposeNodeScenarios(lifeTreeId, nodeId, lifetreeDetail?.language || 'vi');
      setScenarioProposals(response.proposals);
      setCreatedProposalIds(new Set());
      setShowScenarioProposalsModal(true);
      toast.success('Đề xuất Scenario thành công');
    } catch (error: any) {
      toast.error('Đề xuất Scenario thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setProposingScenarios(false);
    }
  };

  const handleGenerateSummary = async (lifetreeDetailOverride?: LifetreeDetailResponse | null) => {
    if (!lifeTreeId || !nodeId) return;

    try {
      setGeneratingSummary(true);
      const response = await lifetreeApi.generateNodeSummary(lifeTreeId, nodeId, (lifetreeDetailOverride || lifetreeDetail)?.language || 'vi');
      
      // Update form value
      form.setFieldsValue({
        summary: response.summary
      });
      
      // Cập nhật lại nodeDetail state để đồng bộ với database (đã lưu ở backend)
      setNodeDetail(prev => prev ? { ...prev, summary: response.summary } : prev);
      
      toast.success('Đã tự động tạo và lưu tóm tắt nội dung node');
    } catch (error: any) {
      toast.error('Tự động tóm tắt thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleSelectScenarioProposal = async (proposal: NodeScenarioProposal) => {
    if (!lifeTreeId || !nodeId) return;

    try {
      setProcessingProposalIds(prev => new Set(prev).add(proposal.id));

      // Step 1: Create Sample from Proposal
      const sampleData: CreateScenarioSampleRequest = {
        directive: proposal.directive,
        characterStateIds: proposal.characterStateIds || [],
      };

      const sampleResponse = await lifetreeApi.createScenarioSample(lifeTreeId, nodeId, sampleData);

      // Step 2: Create Scenario from Sample
      const scenarioData: CreateScenarioRequest = {
        name: sampleResponse.name,
        description: sampleResponse.description || undefined,
        location: sampleResponse.location,
        locationDescription: sampleResponse.locationDescription || undefined,
        time: sampleResponse.time,
        npcIds: sampleResponse.npcIds || [],
        openingScene: sampleResponse.openingScene,
        openingLines: sampleResponse.openingLines || [],
        firstMission: sampleResponse.firstMission || undefined,
      };

      await lifetreeApi.createScenario(lifeTreeId, nodeId, scenarioData);

      setCreatedProposalIds(prev => new Set(prev).add(proposal.id));

      toast.success(`Scenario "${proposal.title}" đã được tạo thành công`);
      
      // Update data after success to reflect new scenarios
      await fetchData(true);
    } catch (error: any) {
      toast.error(`Tạo scenario "${proposal.title}" thất bại`, error.response?.data?.messages?.[0] || error.message);
    } finally {
      setProcessingProposalIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(proposal.id);
        return newSet;
      });
    }
  };

  const handleCreateScenario = async () => {
    if (!lifeTreeId || !nodeId) return;

    try {
      const formValues = await createScenarioForm.validateFields();
      setCreatingScenario(true);

      const scenarioData: CreateScenarioRequest = {
        name: formValues.name,
        description: formValues.description || undefined,
        location: formValues.location,
        locationDescription: formValues.locationDescription || undefined,
        time: formValues.time,
        npcIds: formValues.npcIds || [],
        openingScene: formValues.openingScene,
        openingLines: formValues.openingLines || [],
        firstMission: formValues.firstMission || undefined,
      };

      await lifetreeApi.createScenario(lifeTreeId, nodeId, scenarioData);
      
      toast.success('Tạo scenario thành công');
      setShowCreateScenarioModal(false);
      createScenarioForm.resetFields();
      setSampleScenarioData(null);
      await fetchData(true);
    } catch (error: any) {
      toast.error('Tạo scenario thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setCreatingScenario(false);
    }
  };

  const handleGenerateSampleScenario = async () => {
    if (!lifeTreeId || !nodeId) return;

    try {
      const formValues = await sampleScenarioForm.validateFields();
      setGeneratingSample(true);

      const sampleData: CreateScenarioSampleRequest = {
        directive: formValues.directive,
        characterStateIds: formValues.characterStateIds || [],
      };

      const sampleResponse = await lifetreeApi.createScenarioSample(lifeTreeId, nodeId, sampleData);
      setSampleScenarioData(sampleResponse);
      
      createScenarioForm.setFieldsValue({
        name: sampleResponse.name,
        description: sampleResponse.description,
        location: sampleResponse.location,
        locationDescription: sampleResponse.locationDescription,
        time: sampleResponse.time,
        npcIds: sampleResponse.npcIds,
        openingScene: sampleResponse.openingScene,
        openingLines: sampleResponse.openingLines,
        firstMission: sampleResponse.firstMission,
      });

      setShowSampleScenarioModal(false);
      setShowCreateScenarioModal(true);
      toast.success('Tạo sample scenario thành công');
    } catch (error: any) {
      toast.error('Tạo sample scenario thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setGeneratingSample(false);
    }
  };

  const handleUpdateScenario = async () => {
    if (!lifeTreeId || !nodeId || !editingScenario) return;
    try {
      const formValues = await editScenarioForm.validateFields();
      setUpdatingScenario(true);
      const updateData: UpdateScenarioRequest = {
        name: formValues.name,
        description: formValues.description || undefined,
        location: formValues.location,
        locationDescription: formValues.locationDescription || undefined,
        time: formValues.time,
        npcIds: formValues.npcIds || [],
        openingScene: formValues.openingScene,
        openingLines: formValues.openingLines || [],
        firstMission: formValues.firstMission || undefined,
      };
      await lifetreeApi.updateScenario(lifeTreeId, nodeId, editingScenario.id, updateData);
      toast.success('Cập nhật scenario thành công');
      setShowEditScenarioModal(false);
      editScenarioForm.resetFields();
      setEditingScenario(null);
      await fetchData(true);
    } catch (error: any) {
      toast.error('Cập nhật scenario thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setUpdatingScenario(false);
    }
  };

  const handleActivateScenario = async (scenarioId: string) => {
    if (!lifeTreeId || !nodeId) return;
    try {
      setActivatingScenarioIds(prev => new Set(prev).add(scenarioId));
      await lifetreeApi.activateScenario(lifeTreeId, nodeId, scenarioId);
      toast.success('Kích hoạt scenario thành công');
      
      // Cập nhật state cục bộ
      setNodeDetail(prev => {
        if (!prev) return prev;
        const newScenarios = prev.scenarios.map(s => 
          s.id === scenarioId ? { ...s, status: 'active' } : s
        );
        return { ...prev, scenarios: newScenarios as any };
      });
    } catch (error: any) {
      toast.error('Kích hoạt scenario thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setActivatingScenarioIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(scenarioId);
        return newSet;
      });
    }
  };

  const handleQuickGenScenarioImage = async (scenario: NodeScenario) => {
    if (!lifeTreeId || !nodeId) return;

    try {
      setQuickGenScenarioIds(prev => new Set(prev).add(scenario.id));
      
      // Step 1: Generate prompt
      const promptRes = await lifetreeApi.createScenarioImagePrompt(lifeTreeId, nodeId, scenario.id, {});

      // Step 2: Generate image with the prompt
      const imageRes = await lifetreeApi.createScenarioImageWithPredefinedPrompt(
        lifeTreeId,
        nodeId,
        scenario.id,
        { description: promptRes.technicalPrompt || promptRes.description }
      );

      // Step 3: Approve the generated image
      await lifetreeApi.approveScenarioImage(lifeTreeId, nodeId, scenario.id, {
        imageId: imageRes.id,
      });

      // Step 4: Activate scenario
      await lifetreeApi.activateScenario(lifeTreeId, nodeId, scenario.id);

      toast.success(`Đã hoàn thành tạo ảnh và kích hoạt kịch bản "${scenario.name}"`);
      await fetchData(true);
    } catch (error: any) {
      console.error('Error in quick gen scenario image:', error);
      toast.error(`Lỗi khi tạo ảnh nhanh cho "${scenario.name}"`, error.response?.data?.messages?.[0] || error.message);
    } finally {
      setQuickGenScenarioIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(scenario.id);
        return newSet;
      });
    }
  };

  const handleDeployScenarios = async (nodeDetailOverride?: NodeDetailResponse | null) => {
    const effectiveNodeDetail = nodeDetailOverride || nodeDetail;
    if (!lifeTreeId || !nodeId || !effectiveNodeDetail) return;

    const scenariosToDeploy = effectiveNodeDetail.scenarios.filter(s => s.status === 'draft');
    if (scenariosToDeploy.length === 0) {
      toast.info('Không có kịch bản nào cần triển khai.');
      return;
    }

    setDeployingScenarios(true);
    setDeploymentScenarioProgress({
      total: scenariosToDeploy.length,
      current: 0,
      status: 'Bắt đầu triển khai kịch bản...',
    });

    try {
      for (let i = 0; i < scenariosToDeploy.length; i++) {
        const scenario = scenariosToDeploy[i];
        
        // Update progress
        setDeploymentScenarioProgress({
          total: scenariosToDeploy.length,
          current: i + 1,
          status: `Đang triển khai: ${scenario.name}...`,
        });

        // Skip if already has image and is active (redundant check)
        if (scenario.status === 'active' && scenario.imageUrl) continue;

        // Step 1: Generate prompt (if not already have enough info or just always re-gen for consistency)
        setDeploymentScenarioProgress(prev => prev ? { ...prev, status: `[${i+1}/${scenariosToDeploy.length}] Đang tạo prompt cho ${scenario.name}...` } : null);
        const promptRes = await lifetreeApi.createScenarioImagePrompt(lifeTreeId, nodeId, scenario.id, {});

        // Step 2: Generate image with the prompt
        // Backend handle reference images (img2img) automatically from characters in scenario
        setDeploymentScenarioProgress(prev => prev ? { ...prev, status: `[${i+1}/${scenariosToDeploy.length}] Đang tạo ảnh cho ${scenario.name}...` } : null);
        const imageRes = await lifetreeApi.createScenarioImageWithPredefinedPrompt(
          lifeTreeId,
          nodeId,
          scenario.id,
          { description: promptRes.technicalPrompt || promptRes.description }
        );

        // Step 3: Approve the generated image (this sets scenario.imageUrl)
        setDeploymentScenarioProgress(prev => prev ? { ...prev, status: `[${i+1}/${scenariosToDeploy.length}] Đang duyệt ảnh cho ${scenario.name}...` } : null);
        await lifetreeApi.approveScenarioImage(lifeTreeId, nodeId, scenario.id, {
          imageId: imageRes.id,
        });

        // Step 4: Activate scenario
        setDeploymentScenarioProgress(prev => prev ? { ...prev, status: `[${i+1}/${scenariosToDeploy.length}] Đang kích hoạt ${scenario.name}...` } : null);
        await lifetreeApi.activateScenario(lifeTreeId, nodeId, scenario.id);
      }

      toast.success('Triển khai tất cả kịch bản thành công!');
      await fetchData(true);
    } catch (error: any) {
      toast.error('Lỗi khi triển khai kịch bản', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setDeployingScenarios(false);
      setDeploymentScenarioProgress(null);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    toast.error('Chưa hỗ trợ', 'Chức năng xóa scenario chưa được triển khai.');
  };

  const handleFetchNodeImages = useCallback(async () => {
    if (!lifeTreeId || !nodeId) return;
    try {
      setIsFetchingNodeImages(true);
      // Giả sử có API lấy danh sách ảnh của node, Phase 2 sẽ hiện thực
      // Hiện tại lấy từ nodeDetail.scenarios/characterStates nếu cần, 
      // nhưng đây là tab "Tạo ảnh mới" cho riêng node nên cần API riêng.
      const response = await lifetreeApi.getNodeImages(lifeTreeId, nodeId);
      setNodeImages(response);
    } catch (error: any) {
      console.error('Failed to fetch node images:', error);
    } finally {
      setIsFetchingNodeImages(false);
    }
  }, [lifeTreeId, nodeId]);

  const handleGenerateNodeImageConcept = async () => {
    if (!lifeTreeId || !nodeId) return;
    try {
      setIsGeneratingConcept(true);
      const response = await lifetreeApi.createNodeImagePrompt(lifeTreeId, nodeId);
      setAiConcept(response);
      toast.success('AI đã đề xuất concept cho ảnh node');
    } catch (error: any) {
      toast.error('Đề xuất concept thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setIsGeneratingConcept(false);
    }
  };

  const handleGenerateNodeImage = async (description: string) => {
    if (!lifeTreeId || !nodeId) return;
    try {
      setIsGeneratingNodeImage(true);
      // Lấy involvedCharacterIds từ concept nếu có
      const involvedCharacterIds = (aiConcept as any)?.involvedCharacterIds || [];
      
      await lifetreeApi.createNodeImageWithPredefinedPrompt(lifeTreeId, nodeId, { 
        description,
        involvedCharacterIds
      });
      toast.success('Đã hoàn tất sinh ảnh node');
      await handleFetchNodeImages();
    } catch (error: any) {
      toast.error('Sinh ảnh thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setIsGeneratingNodeImage(false);
    }
  };

  const handleApproveScenarioImage = async () => {
    const targetScenario = selectedScenarioForImage;
    const targetImage = generatedScenarioImage;
    
    if (!lifeTreeId || !nodeId || !targetScenario || !targetImage) return;

    try {
      setApprovingScenarioImage(true);
      await lifetreeApi.approveScenarioImage(lifeTreeId, nodeId, targetScenario.id, {
        imageId: targetImage.id,
      });
      
      toast.success('Duyệt ảnh thành công');
      setShowScenarioImagePreviewModal(false);
      setSelectedScenarioForImage(null);
      setGeneratedScenarioImage(null);
      
      setNodeDetail(prev => {
        if (!prev) return prev;
        const newScenarios = prev.scenarios.map(s => {
          if (s.id === targetScenario.id) {
            const newImages = s.images.map(img => ({
              ...img,
              status: img.id === targetImage.id ? 'active' : img.status
            } as StateImage));
            return { ...s, imageUrl: targetImage.url, images: newImages };
          }
          return s;
        });
        return { ...prev, scenarios: newScenarios };
      });
    } catch (error: any) {
      toast.error('Duyệt ảnh thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setApprovingScenarioImage(false);
    }
  };

  const handleNPCCharacterChange = (rowId: string, characterId: string) => {
    if (characterId === 'CREATE_NEW') {
      setShowCreateCharacterModal(true);
      return;
    }
    
    setNpcRows(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, characterId, stateId: null }
        : row
    ));
  };

  const handleNPCStateChange = async (rowId: string, stateId: string) => {
    const row = npcRows.find(r => r.id === rowId);
    if (!row || !row.characterId) return;

    if (stateId === 'CREATE_NEW_STATE') {
      setSelectedCharacterForState(row.characterId);
      setMethodSelectionModalOpen(true);
      return;
    }

    await handleAddCharacterState(row.characterId, stateId);
    
    setNpcRows(prev => {
      const updatedRows = prev.map(r => 
        r.id === rowId ? { ...r, stateId } : r
      );
      
      const isLastRow = prev[prev.length - 1].id === rowId;
      if (isLastRow) {
        const newRowId = Date.now().toString();
        return [...updatedRows, { id: newRowId, characterId: null, stateId: null }];
      }
      
      return updatedRows;
    });
  };

  const handleRemoveNPCRow = async (rowId: string) => {
    const row = npcRows.find(r => r.id === rowId);
    if (!row || !row.stateId) return;

    await handleRemoveCharacterState(row.stateId);
    
    setNpcRows(prev => {
      const filtered = prev.filter(r => r.id !== rowId);
      const hasEmptyRow = filtered.some(r => r.characterId === null && r.stateId === null);
      if (!hasEmptyRow) {
        const newRowId = `empty-${Date.now()}`;
        return [...filtered, { id: newRowId, characterId: null, stateId: null }];
      }
      return filtered;
    });
  };

  const handleGenerateScenarioPrompt = async (description?: string): Promise<CreateScenarioImagePromptResponse> => {
    if (!lifeTreeId || !nodeId || !selectedScenarioForImage) {
      throw new Error('Missing context for generating prompt');
    }

    try {
      setGeneratingScenarioPrompt(true);
      return await lifetreeApi.createScenarioImagePrompt(lifeTreeId, nodeId, selectedScenarioForImage.id, {
        description,
      });
    } catch (error: any) {
      toast.error('Tạo prompt thất bại', error.response?.data?.messages?.[0] || error.message);
      throw error;
    } finally {
      setGeneratingScenarioPrompt(false);
    }
  };

  const handleCreateScenarioImageWithPrompt = async (description: string) => {
    if (!lifeTreeId || !nodeId || !selectedScenarioForImage) return;

    try {
      setCreatingScenarioImage(true);
      const imageResponse = await lifetreeApi.createScenarioImageWithPredefinedPrompt(
        lifeTreeId,
        nodeId,
        selectedScenarioForImage.id,
        { description }
      );

      const mappedImage = {
        ...imageResponse,
        unlocked: true,
        tags: (imageResponse as any).tags || [],
      } as StateImage;

      setNodeDetail(prev => {
        if (!prev) return prev;
        const updatedScenarios = prev.scenarios.map(scenario => {
          if (scenario.id === selectedScenarioForImage.id) {
            return { ...scenario, images: [...(scenario.images || []), mappedImage] };
          }
          return scenario;
        });
        return { ...prev, scenarios: updatedScenarios } as NodeDetailResponse;
      });

      setGeneratedScenarioImage(imageResponse);
      setShowCreateScenarioImageModal(false);
      setShowScenarioImagePreviewModal(true);
      toast.success('Tạo ảnh thành công');
    } catch (error: any) {
      toast.error('Tạo ảnh thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setCreatingScenarioImage(false);
    }
  };

  useEffect(() => {
    if (lifetreeDetail) {
      const npcStates = pendingCharacterStateIds
        .map(stateId => {
          for (const character of lifetreeDetail.characters) {
            const state = character.states.find(s => s.id === stateId);
            if (state && character.type === 'npc') {
              return { characterId: character.id, stateId: state.id };
            }
          }
          return null;
        })
        .filter(Boolean) as { characterId: string; stateId: string }[];

      const initialRows: NPCRow[] = npcStates.map((npcState, index) => ({
        id: `existing-${index}-${Date.now()}`,
        characterId: npcState.characterId,
        stateId: npcState.stateId,
      }));
      
      const newRowId = `empty-${Date.now()}`;
      initialRows.push({ id: newRowId, characterId: null, stateId: null });
      setNpcRows(initialRows);
    }
  }, [pendingCharacterStateIds, lifetreeDetail]);

  // Helpers
  const getAvailableNPCsForScenario = () => {
    if (!lifetreeDetail) return [];
    const npcCharacterIds: string[] = [];
    pendingCharacterStateIds.forEach(stateId => {
      for (const character of lifetreeDetail.characters) {
        const state = character.states.find(s => s.id === stateId);
        if (state && character.type === 'npc' && !npcCharacterIds.includes(character.id)) {
          npcCharacterIds.push(character.id);
        }
      }
    });
    
    return npcCharacterIds.map(characterId => {
      const character = lifetreeDetail.characters.find(c => c.id === characterId);
      return character ? { id: character.id, name: character.name, avatar: character.imageUrl } : null;
    }).filter(Boolean) as { id: string; name: string; avatar: string | null }[];
  };

  const getAvailableNPCCharacters = (excludeRowId?: string) => {
    if (!lifetreeDetail) return [];
    const selectedCharacterIds = npcRows
      .filter(row => row.id !== excludeRowId && row.characterId)
      .map(row => row.characterId);
    
    return lifetreeDetail.characters.filter(char => 
      char.type === 'npc' && !selectedCharacterIds.includes(char.id)
    );
  };

  const getAvailableStatesForCharacter = (characterId: string, includeStateId?: string | null) => {
    if (!lifetreeDetail) return [];
    const character = lifetreeDetail.characters.find(char => char.id === characterId);
    if (!character) return [];
    
    const usedStateIds = new Set<string>();
    pendingCharacterStateIds.forEach(id => usedStateIds.add(id));
    lifetreeDetail.nodes.forEach(node => {
      node.characterStateIds.forEach(id => usedStateIds.add(id));
    });
    
    return character.states.filter(state => !usedStateIds.has(state.id) || state.id === includeStateId);
  };

  const getCharacterNameById = (characterId: string) => {
    const character = lifetreeDetail?.characters.find(c => c.id === characterId);
    return character?.name || characterId;
  };

  const getCharacterStatesForAI = (): CharacterStateWithRelationships[] => {
    if (!selectedCharacterForState || !lifetreeDetail) return [];
    const character = lifetreeDetail.characters.find(char => char.id === selectedCharacterForState);
    if (!character) return [];
    return character.states.map(state => ({ ...state, relationships: [], images: [] }));
  };

  const getSelectedCharacterName = () => {
    if (!selectedCharacterForState || !lifetreeDetail) return '';
    const character = lifetreeDetail.characters.find(char => char.id === selectedCharacterForState);
    return character?.name || '';
  };

  const openEditScenarioModal = (scenario: NodeScenario) => {
    setEditingScenario(scenario);
    editScenarioForm.setFieldsValue({
      name: scenario.name,
      description: scenario.description || undefined,
      location: scenario.location || undefined,
      locationDescription: scenario.locationDescription || undefined,
      time: scenario.time || undefined,
      npcIds: scenario.npcIds || [],
      openingScene: scenario.openingScene || undefined,
      openingLines: scenario.openingLines || [],
      firstMission: scenario.firstMission || undefined,
    });
    setShowEditScenarioModal(true);
  };

  const openCreateScenarioImageModal = (scenario: NodeScenario) => {
    setSelectedScenarioForImage(scenario);
    setGeneratedScenarioImage(null);
    setShowCreateScenarioImageModal(true);
  };

  useEffect(() => {
    if (lifeTreeId && nodeId) {
      fetchData();
      handleFetchNodeImages();
    }
  }, [lifeTreeId, nodeId, fetchData, handleFetchNodeImages]);

  useEffect(() => {
    if (nodeDetail && lifetreeDetail) {
      processCharacterStatesData(nodeDetail, lifetreeDetail);
    }
  }, [pendingCharacterStateIds, nodeDetail, lifetreeDetail, processCharacterStatesData]);

  return {
    form,
    createScenarioForm,
    sampleScenarioForm,
    editScenarioForm,
    nodeDetail,
    lifetreeDetail,
    characterStatesData,
    loading,
    refreshing,
    saving,
    hasChanges,
    hasStateChanges,
    activatingNode,
    activatingStates,
    planningCharacters,
    planModalOpen,
    editablePlanCharacters,
    creatingCharactersFromPlan,
    creatingStatesFromPlan,
    deployingRelationships,
    deployingImages,
    formalizingCharacters,
    deployingCharacters,
    deployingFull,
    resettingCharacters,
    deployingScenarios,
    autoDeploying,
    autoDeploymentProgress,
    deploymentProgress,
    deploymentScenarioProgress,
    showCreateCharacterModal,
    creatingCharacter,
    creatingState,
    selectedCharacterForState,
    methodSelectionModalOpen,
    createNewStateModalOpen,
    aiDescriptionModalOpen,
    stateInitialData,
    showCreateScenarioModal,
    showSampleScenarioModal,
    creatingScenario,
    generatingSample,
    sampleScenarioData,
    showScenarioImagePreviewModal,
    selectedScenarioForImage,
    generatedScenarioImage,
    creatingScenarioImage,
    approvingScenarioImage,
    showEditScenarioModal,
    editingScenario,
    updatingScenario,
    activatingScenarioIds,
    quickGenScenarioIds,
    npcRows,
    showCreateScenarioImageModal,
    generatingScenarioPrompt,
    proposingScenarios,
    generatingSummary,
    scenarioProposals,
    showScenarioProposalsModal,
    processingProposalIds,
    createdProposalIds,
    nodeImages,
    aiConcept,
    isGeneratingConcept,
    isGeneratingNodeImage,
    isFetchingNodeImages,
    setPlanModalOpen,
    setShowCreateCharacterModal,
    setMethodSelectionModalOpen,
    setSelectedCharacterForState,
    setShowCreateScenarioModal,
    setSampleScenarioData,
    setShowSampleScenarioModal,
    setShowScenarioImagePreviewModal,
    setGeneratedScenarioImage,
    setSelectedScenarioForImage,
    setShowEditScenarioModal,
    setEditingScenario,
    setShowScenarioProposalsModal,
    setAIDescriptionModalOpen,
    setCreateNewStateModalOpen,
    setStateInitialData,
    setShowCreateScenarioImageModal,
    handlePlanCharacters,
    handleFormalizeCharacters,
    updatePlanCharacter,
    handleCreateCharactersFromPlan,
    handleCreateStatesFromPlan,
    handleDeployRelationships,
    handleDeployImages,
    handleDeployCharacters,
    handleResetCharacters,
    handleDeployScenarios,
    handleAutoDeployNode,
    handleSave,
    handleSetCoverImage,
    handleActivateNode,
    handleActivateState,
    handleCreateCharacter,
    handleMethodSelection,
    handleAICompletion,
    handleCreateNewState,
    handleAddCharacterState,
    handleRemoveCharacterState,
    handleProposeScenarios,
    handleGenerateSummary,
    handleSelectScenarioProposal,
    handleCreateScenario,
    handleGenerateSampleScenario,
    handleUpdateScenario,
    handleActivateScenario,
    handleQuickGenScenarioImage,
    handleDeleteScenario,
    handleApproveScenarioImage,
    handleGenerateNodeImageConcept,
    handleGenerateNodeImage,
    handleFetchNodeImages,
    handleNPCCharacterChange,
    handleNPCStateChange,
    handleRemoveNPCRow,
    handleGenerateScenarioPrompt,
    handleCreateScenarioImageWithPrompt,
    handleFormChange: () => setHasChanges(true),
    getAvailableNPCsForScenario,
    getAvailableNPCCharacters,
    getAvailableStatesForCharacter,
    getCharacterNameById,
    getCharacterStatesForAI,
    getSelectedCharacterName,
    openEditScenarioModal,
    openCreateScenarioImageModal,
  };
};
